import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { getUsers } from './supabaseClient';
import './Users.css';

const Users = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch real users data from Supabase
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all users from Supabase
        const usersData = await getUsers();

        // Transform users data to match component format
        const formattedUsers = usersData.map((user) => {
          // Format registration date
          const registrationDate = user.registration_date
            ? new Date(user.registration_date).toLocaleDateString('en-NG', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit' 
              })
            : 'N/A';

          // Format last login date
          const lastLogin = user.last_login
            ? new Date(user.last_login).toLocaleDateString('en-NG', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : 'Never';

          // Format user ID (show first 8 characters of UUID)
          const userId = user.id ? user.id.substring(0, 8).toUpperCase() : 'N/A';

          return {
            id: userId,
            userId: user.id, // Keep full UUID for navigation
            name: user.name || 'Unknown User',
            email: user.email || 'N/A',
            phone: user.phone || 'N/A',
            registrationDate: registrationDate,
            lastLogin: lastLogin,
            status: user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Active',
            rawUser: user // Keep raw data for details
          };
        });

        setAllUsers(formattedUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err.message || 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Placeholder users data (fallback if no data)
  const placeholderUsers = [
    {
      id: 'USR001',
      name: 'John Doe',
      email: 'john.doe@example.com',
      phone: '+234 801 234 5678',
      registrationDate: '2024-01-15',
      status: 'Active'
    },
    {
      id: 'USR002',
      name: 'Jane Smith',
      email: 'jane.smith@example.com',
      phone: '+234 802 345 6789',
      registrationDate: '2024-02-20',
      status: 'Active'
    },
    {
      id: 'USR003',
      name: 'Michael Brown',
      email: 'michael.brown@example.com',
      phone: '+234 803 456 7890',
      registrationDate: '2024-03-10',
      status: 'Suspended'
    },
    {
      id: 'USR004',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@example.com',
      phone: '+234 804 567 8901',
      registrationDate: '2024-04-05',
      status: 'Active'
    },
    {
      id: 'USR005',
      name: 'David Williams',
      email: 'david.williams@example.com',
      phone: '+234 805 678 9012',
      registrationDate: '2024-05-12',
      status: 'Active'
    },
    {
      id: 'USR006',
      name: 'Emily Davis',
      email: 'emily.davis@example.com',
      phone: '+234 806 789 0123',
      registrationDate: '2024-06-18',
      status: 'Active'
    },
    {
      id: 'USR007',
      name: 'Robert Miller',
      email: 'robert.miller@example.com',
      phone: '+234 807 890 1234',
      registrationDate: '2024-07-22',
      status: 'Suspended'
    },
    {
      id: 'USR008',
      name: 'Lisa Anderson',
      email: 'lisa.anderson@example.com',
      phone: '+234 808 901 2345',
      registrationDate: '2024-08-30',
      status: 'Active'
    },
    {
      id: 'USR009',
      name: 'James Wilson',
      email: 'james.wilson@example.com',
      phone: '+234 809 012 3456',
      registrationDate: '2024-09-14',
      status: 'Active'
    },
    {
      id: 'USR010',
      name: 'Maria Garcia',
      email: 'maria.garcia@example.com',
      phone: '+234 810 123 4567',
      registrationDate: '2024-10-08',
      status: 'Active'
    },
    {
      id: 'USR011',
      name: 'William Martinez',
      email: 'william.martinez@example.com',
      phone: '+234 811 234 5678',
      registrationDate: '2024-11-25',
      status: 'Active'
    },
    {
      id: 'USR012',
      name: 'Patricia Taylor',
      email: 'patricia.taylor@example.com',
      phone: '+234 812 345 6789',
      registrationDate: '2024-12-01',
      status: 'Suspended'
    },
  ];

  // Use real data if available, otherwise use placeholder (for development)
  const usersToDisplay = allUsers.length > 0 ? allUsers : placeholderUsers;

  // Filter users based on search and status
  const filteredUsers = usersToDisplay.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone.includes(searchQuery);
    
    const matchesStatus = statusFilter === 'all' || user.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page on filter
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleExport = () => {
    // Placeholder for export functionality
    console.log('Exporting users to CSV...');
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedUser(null);
  };

  return (
    <AdminLayout pageTitle="Users">
      <div className="admin-users-page">
        {/* Page Title */}
        <div className="admin-users-header">
          <h1 className="admin-users-title">Users</h1>
          <p className="admin-users-subtitle">Manage registered customers</p>
        </div>

        {/* Top Controls Bar */}
        <div className="admin-users-controls">
          <div className="admin-controls-left">
            <div className="admin-search-wrapper">
              <input
                type="text"
                className="admin-search-input"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="admin-search-icon">🔍</span>
            </div>
            
            <select
              className="admin-filter-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>

            <select className="admin-filter-select">
              <option value="">Date Range</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          <div className="admin-controls-right">
            <button className="admin-export-button" onClick={handleExport}>
              <span className="admin-export-icon">📥</span>
              Export CSV
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="admin-empty-state">
            <div className="admin-empty-icon">⏳</div>
            <h3 className="admin-empty-title">Loading users...</h3>
            <p className="admin-empty-text">Please wait while we fetch your data</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="admin-empty-state">
            <div className="admin-empty-icon">⚠️</div>
            <h3 className="admin-empty-title">Error loading users</h3>
            <p className="admin-empty-text">{error}</p>
            <button 
              className="admin-action-button"
              onClick={() => window.location.reload()}
              style={{ marginTop: '1rem' }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Users Table */}
        {!loading && !error && paginatedUsers.length > 0 ? (
          <>
            <div className="admin-users-table-wrapper">
              {/* Desktop Table */}
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Registration Date</th>
                    <th>Last Login</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user) => (
                    <tr key={user.id} className="admin-users-table-row">
                      <td className="admin-table-cell admin-table-id">{user.id}</td>
                      <td className="admin-table-cell">{user.name}</td>
                      <td className="admin-table-cell">{user.email}</td>
                      <td className="admin-table-cell">{user.phone}</td>
                      <td className="admin-table-cell">{user.registrationDate}</td>
                      <td className="admin-table-cell" style={{ fontSize: '0.9em', color: '#666' }}>
                        {user.lastLogin || 'Never'}
                      </td>
                      <td className="admin-table-cell">
                        <span className={`admin-status-badge admin-status-${user.status.toLowerCase()}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <div className="admin-actions-menu">
                          <button
                            className="admin-action-button"
                            onClick={() => handleViewDetails(user)}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className="admin-users-cards">
                {paginatedUsers.map((user) => (
                  <div key={user.id} className="admin-user-card">
                    <div className="admin-user-card-header">
                      <div className="admin-user-card-name">{user.name}</div>
                      <span className={`admin-status-badge admin-status-${user.status.toLowerCase()}`}>
                        {user.status}
                      </span>
                    </div>
                    <div className="admin-user-card-body">
                      <div className="admin-user-card-row">
                        <span className="admin-user-card-label">User ID:</span>
                        <span className="admin-user-card-value">{user.id}</span>
                      </div>
                      <div className="admin-user-card-row">
                        <span className="admin-user-card-label">Email:</span>
                        <span className="admin-user-card-value">{user.email}</span>
                      </div>
                      <div className="admin-user-card-row">
                        <span className="admin-user-card-label">Phone:</span>
                        <span className="admin-user-card-value">{user.phone}</span>
                      </div>
                      <div className="admin-user-card-row">
                        <span className="admin-user-card-label">Registered:</span>
                        <span className="admin-user-card-value">{user.registrationDate}</span>
                      </div>
                      {user.lastLogin && (
                        <div className="admin-user-card-row">
                          <span className="admin-user-card-label">Last Login:</span>
                          <span className="admin-user-card-value">{user.lastLogin}</span>
                        </div>
                      )}
                    </div>
                    <div className="admin-user-card-actions">
                      <button
                        className="admin-action-button"
                        onClick={() => handleViewDetails(user)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
              <div className="admin-pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div className="admin-pagination-controls">
                <select
                  className="admin-rows-select"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="admin-page-numbers">
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first, last, current, and pages around current
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => {
                      // Add ellipsis if needed
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="admin-page-ellipsis">...</span>}
                          <button
                            className={`admin-page-button ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : !loading && !error ? (
          /* Empty State */
          <div className="admin-empty-state">
            <div className="admin-empty-icon">👥</div>
            <h3 className="admin-empty-title">No users found</h3>
            <p className="admin-empty-text">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'No users have been registered yet'}
            </p>
          </div>
        ) : null}

        {/* User Details Modal/Popup */}
        {showDetailsModal && selectedUser && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px',
              backdropFilter: 'blur(4px)'
            }}
            onClick={handleCloseModal}
          >
            <div 
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                padding: 0,
                maxWidth: '900px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '24px 30px',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: '16px 16px 0 0'
              }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: 'white' }}>
                    User Details
                  </h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                    User ID: {selectedUser.id}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                >
                  ×
                </button>
              </div>

              {/* Content Area */}
              <div style={{
                padding: '30px',
                overflowY: 'auto',
                flex: 1,
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ display: 'grid', gap: '20px' }}>
                  {/* User Profile Card */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #e9ecef'
                  }}>
                    <h3 style={{
                      margin: '0 0 16px 0',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '20px' }}>👤</span>
                      Profile Information
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Full Name</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedUser.name}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Email Address</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedUser.email}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Phone Number</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedUser.phone}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>User ID</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedUser.id}</div>
                      </div>
                    </div>
                  </div>

                  {/* Account Status Card */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #e9ecef'
                  }}>
                    <h3 style={{
                      margin: '0 0 16px 0',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '20px' }}>📊</span>
                      Account Status
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Status</div>
                        <div>
                          <span className={`admin-status-badge admin-status-${selectedUser.status.toLowerCase()}`}>
                            {selectedUser.status}
                          </span>
                        </div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Registration Date</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedUser.registrationDate}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Last Login</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedUser.lastLogin || 'Never'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Information Card */}
                  {selectedUser.rawUser && (
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      border: '1px solid #e9ecef'
                    }}>
                      <h3 style={{
                        margin: '0 0 16px 0',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>ℹ️</span>
                        Additional Information
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                        <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Full User ID (UUID)</div>
                          <div style={{ fontSize: '13px', color: '#212529', fontWeight: '500', wordBreak: 'break-all' }}>
                            {selectedUser.rawUser.id || selectedUser.userId || 'N/A'}
                          </div>
                        </div>
                        {selectedUser.rawUser.created_at && (
                          <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Account Created</div>
                            <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>
                              {new Date(selectedUser.rawUser.created_at).toLocaleString('en-NG')}
                            </div>
                          </div>
                        )}
                        {selectedUser.rawUser.updated_at && (
                          <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Last Updated</div>
                            <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>
                              {new Date(selectedUser.rawUser.updated_at).toLocaleString('en-NG')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div style={{
                padding: '20px 30px',
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #e9ecef',
                display: 'flex',
                justifyContent: 'flex-end',
                borderRadius: '0 0 16px 16px'
              }}>
                <button
                  onClick={handleCloseModal}
                  style={{
                    padding: '12px 32px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#5568d3';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#667eea';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Users;

